interface Modal {
  modalRef?: HTMLDivElement | null;
}

/**
 * CSS property backup type.
 */
type StyleBackup = Array<{
  property: string;
  value: string;
}>;

/**
 * ModalDescriptor. Associates an open Modal with overlayed aria-hidden children.
 */
type ModalDescriptor = {
  modal: Modal;
  ariaHiddenChildren: Element[];
};

/**
 * Returns an array of element's siblings.
 *
 * @param elem
 * @returns
 */
function getSiblings(elem: Element): Element[] {
  const siblings: Element[] = [];
  let sibling = elem.previousSibling;
  while (sibling != null) {
    if (sibling.nodeType === 1) {
      siblings.push(sibling as Element);
    }
    sibling = sibling.previousSibling;
  }
  sibling = elem.nextSibling;
  while (sibling != null) {
    if (sibling.nodeType === 1) {
      siblings.push(sibling as Element);
    }
    sibling = sibling.nextSibling;
  }
  return siblings;
}

/**
 * Sets aria-hidden=true on Modal ancestors and siblings.
 *
 * @param modal
 * @returns An array of modified elements.
 */
function setAriaHiddenOnBackgroundElements(modal: Modal) {
  if (!modal.modalRef) {
    return [];
  }
  const ariaHiddenElements: Element[] = [];
  let node: Element | null = modal.modalRef.parentElement as Element;

  do {
    if (!node) {
      break;
    }
    const siblings = getSiblings(node);
    siblings.forEach((elem: Element) => {
      if (elem.tagName.toLowerCase() !== 'script' && elem.getAttribute('aria-hidden') !== 'true') {
        ariaHiddenElements.push(elem);
        elem.setAttribute('aria-hidden', 'true');
      }
    });
    node = node.parentElement;
  } while (node && node.tagName.toLowerCase() !== 'body');

  return ariaHiddenElements;
}

/**
 * Resets the aria-hidden state on previously aria-hidden elements.
 *
 * @param elements
 */
function resetAriaHiddenOnBackgroundElements(elements: Element[]) {
  elements.forEach((elem) => {
    elem.removeAttribute('aria-hidden');
  });
}

export default class ModalManager {
  /**
   * Array of ModalDescriptors managed by ModalManager
   */
  private modals: ModalDescriptor[];
  /**
   * Backup of the main container's (body) CSS props potentially altered by Modal.
   */
  private styleBackup: StyleBackup;

  constructor() {
    this.modals = [];
    this.styleBackup = [];
  }

  /**
   * Registers a modal with ModalManager
   *
   * @param modal
   * @returns
   */
  push(modal: Modal) {
    const modalIndex = this._findModalIndex(modal);

    if (modalIndex !== -1) {
      // Do not try to register the same modal multiple times
      return;
    }

    this.modals.push({
      modal: modal,
      ariaHiddenChildren: setAriaHiddenOnBackgroundElements(modal)
    });

    // if this is the first modal in the container, block container scrolling
    if (this.modals.length === 1) {
      const ownerDocument = this._getOwnerDocument(modal.modalRef);
      const scrollContainer = ownerDocument.body;

      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollBarWidth > 1) {
        // in RTL, scrollbar is on the left
        const scrollBarLeft =
          Math.round(document.documentElement.getBoundingClientRect().left) +
          document.documentElement.scrollLeft;

        if (scrollBarLeft) {
          this.styleBackup.push({
            property: 'padding-left',
            value: scrollContainer.style.paddingLeft
          });
          scrollContainer.style.paddingLeft = `${scrollBarWidth}px`;
        } else {
          this.styleBackup.push({
            property: 'padding-right',
            value: scrollContainer.style.paddingRight
          });
          scrollContainer.style.paddingRight = `${scrollBarWidth}px`;
        }
      }

      this.styleBackup.push({ property: 'overflow', value: scrollContainer.style.overflow });
      this.styleBackup.push({ property: 'overflow-x', value: scrollContainer.style.overflowX });
      this.styleBackup.push({ property: 'overflow-y', value: scrollContainer.style.overflowY });

      scrollContainer.style.overflow = 'hidden';
    }
  }

  /**
   * Unegisters a modal from ModalManager
   *
   * @param modal
   * @returns
   */
  pop(modal: Modal) {
    const modalIndex = this._findModalIndex(modal);

    if (modalIndex === -1) {
      // modal to be removed not found!'
      return;
    }

    if (modalIndex !== this.modals.length - 1) {
      // trying to remove modal out of order!
      return;
    }

    const modalDescriptor = this.modals.pop();

    if (modalDescriptor) {
      resetAriaHiddenOnBackgroundElements(modalDescriptor?.ariaHiddenChildren);
    }

    // if this was the last modal in the container, restore its overflow props
    if (this.modals.length === 0) {
      const ownerDocument = this._getOwnerDocument(modal.modalRef);
      const scrollContainer = ownerDocument.body;

      this.styleBackup.forEach(({ property, value }) => {
        scrollContainer.style.setProperty(property, value);
      });
    }
  }

  isTopModal(modal: Modal): boolean {
    return this.modals.length > 0 && this.modals[this.modals.length - 1] === modal;
  }

  private _findModalIndex(modal: Modal): number {
    let index = -1;
    this.modals.forEach((elem, idx) => {
      if (elem.modal === modal) {
        index = idx;
        return;
      }
    });
    return index;
  }

  private _getOwnerDocument(node: Node | null | undefined): Document {
    return (node && node.ownerDocument) || document;
  }
}
